import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import type { Meeting } from '../../types';

interface Props {
  meeting: Meeting;
  onClose: () => void;
}

export default function InviteBotModal({ meeting, onClose }: Props) {
  const { inviteBot, toast, botServerOnline } = useApp();
  const { isMobile } = useBreakpoint();
  const [joinMode, setJoinMode] = useState<'now' | 'scheduled'>('now');
  const [slot, setSlot] = useState(meeting.time || '');
  const [meetUrl, setMeetUrl] = useState(meeting.meetUrl || '');
  const [loading, setLoading] = useState(false);

  async function handleInvite() {
    if (!meetUrl.trim()) { toast('Meeting URL is required', 'error'); return; }
    if (joinMode === 'scheduled' && !slot) { toast('Please enter a time slot', 'error'); return; }

    setLoading(true);
    try {
      await inviteBot(meeting.id, meetUrl, joinMode, joinMode === 'scheduled' ? slot : undefined);
      toast(joinMode === 'now' ? 'Bot is joining the meeting…' : `Bot scheduled for ${slot}`, 'success');
      onClose();
    } catch (err) {
      toast((err as Error).message || 'Failed to invite bot', 'error');
    } finally {
      setLoading(false);
    }
  }

  const PLATFORM_ICONS: Record<string, string> = {
    gmeet: 'ti-brand-google',
    zoom:  'ti-video',
    teams: 'ti-brand-teams',
    other: 'ti-link',
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,27,45,.6)', zIndex: 700, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '20px' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'white', borderRadius: isMobile ? '20px 20px 0 0' : '16px', width: '100%', maxWidth: isMobile ? '100%' : '480px', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,.4)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        {isMobile && <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: 'rgba(15,27,45,.2)', margin: '10px auto 0' }} />}

        {/* Header */}
        <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #0F1B2D, #1A2D45)', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(99,102,241,.25)', border: '1px solid rgba(99,102,241,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="ti ti-robot" style={{ color: '#818CF8', fontSize: '18px' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: 'white' }}>Invite AI Notetaker</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meeting.title}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.5)', cursor: 'pointer', fontSize: '22px', padding: '2px', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Bot server status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', borderRadius: '9px', background: botServerOnline ? '#F0FFF4' : '#FFF5F5', border: `1px solid ${botServerOnline ? '#9AE6B4' : '#FEB2B2'}`, marginBottom: '18px' }}>
            <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: botServerOnline ? '#22C55E' : '#EF4444', flexShrink: 0 }} />
            <span style={{ fontSize: '12px', color: botServerOnline ? '#276749' : '#C53030', fontWeight: '600' }}>
              {botServerOnline ? 'Bot server online — ready to join' : 'Bot server offline — start the bot server first'}
            </span>
          </div>

          {/* Meeting URL */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ fontSize: '11px', fontWeight: '700', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: '6px' }}>
              <i className={`ti ${PLATFORM_ICONS[meeting.platform] || 'ti-link'}`} style={{ marginRight: '5px' }} />
              Meeting URL
            </label>
            <input
              value={meetUrl}
              onChange={e => setMeetUrl(e.target.value)}
              placeholder="https://meet.google.com/xxx-xxxx-xxx"
              style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E2E8F0', borderRadius: '8px', fontFamily: 'inherit', fontSize: '13px', outline: 'none', boxSizing: 'border-box', color: '#1A202C' }}
            />
          </div>

          {/* Join mode */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '11px', fontWeight: '700', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: '8px' }}>Join mode</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {(['now', 'scheduled'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => setJoinMode(mode)}
                  style={{
                    padding: '10px 12px', borderRadius: '9px', border: `2px solid ${joinMode === mode ? '#6366F1' : '#E2E8F0'}`,
                    background: joinMode === mode ? '#EEF2FF' : 'white', cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', transition: 'all .15s',
                  }}
                >
                  <i className={`ti ${mode === 'now' ? 'ti-bolt' : 'ti-clock'}`} style={{ fontSize: '18px', color: joinMode === mode ? '#6366F1' : '#718096' }} />
                  <span style={{ fontSize: '12px', fontWeight: '700', color: joinMode === mode ? '#4338CA' : '#4A5568' }}>
                    {mode === 'now' ? 'Join Now' : 'Scheduled'}
                  </span>
                  <span style={{ fontSize: '10px', color: joinMode === mode ? '#6366F1' : '#718096' }}>
                    {mode === 'now' ? 'Bot joins immediately' : 'Bot joins at set time'}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Scheduled time */}
          {joinMode === 'scheduled' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: '6px' }}>Join time (HH:MM)</label>
              <input
                type="time"
                value={slot}
                onChange={e => setSlot(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E2E8F0', borderRadius: '8px', fontFamily: 'inherit', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          )}

          {/* Participants preview */}
          {meeting.participants.length > 0 && (
            <div style={{ padding: '10px 12px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '9px', marginBottom: '18px' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', marginBottom: '7px' }}>Participants</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {meeting.participants.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold)', fontSize: '8px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.ini}</div>
                    <span style={{ fontSize: '11px', color: '#2D3748', fontWeight: '500' }}>{p.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Consent notice */}
          <div style={{ display: 'flex', gap: '8px', padding: '9px 12px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px', marginBottom: '18px' }}>
            <i className="ti ti-info-circle" style={{ color: '#B45309', fontSize: '14px', flexShrink: 0, marginTop: '1px' }} />
            <span style={{ fontSize: '11px', color: '#92400E', lineHeight: 1.5 }}>
              The bot joins as <strong>effyBiz Notetaker</strong> — all participants can see it in the attendee list. Ensure you have consent to record.
            </span>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={onClose}
              style={{ flex: 1, padding: '11px', borderRadius: '9px', border: '1.5px solid #E2E8F0', background: 'white', fontFamily: 'inherit', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: '#4A5568' }}
            >
              Cancel
            </button>
            <button
              onClick={handleInvite}
              disabled={loading || !botServerOnline}
              style={{
                flex: 2, padding: '11px', borderRadius: '9px', border: 'none',
                background: loading || !botServerOnline ? '#CBD5E0' : '#6366F1',
                color: 'white', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700',
                cursor: loading || !botServerOnline ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
              }}
            >
              {loading
                ? <><i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite' }} /> Inviting…</>
                : <><i className={`ti ${joinMode === 'now' ? 'ti-robot' : 'ti-calendar-plus'}`} /> {joinMode === 'now' ? 'Invite Bot Now' : 'Schedule Bot'}</>
              }
            </button>
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
